import React, { useEffect, useMemo, useState } from 'react';
import { Play, Pencil, Trash2, Plus, Loader2, Link2, Lock, Users, Folder, Check, X } from 'lucide-react';
import { api, type QuizSummary } from '../lib/api';
import { ShareButton } from './ShareButton';

interface Props {
  onHost: (quiz: QuizSummary) => void;
  onEdit: (quizId: string) => void;
  onCreate: () => void;
  /** Se avisa tras borrar, para que el catálogo de inicio no muestre un fantasma. */
  onCatalogChange: () => void;
}

/**
 * Las trivias que ha creado quien está mirando su perfil.
 *
 * Incluye las privadas, que no aparecen en el catálogo público: para su autor
 * son justamente las que más cuesta reencontrar, porque no hay ninguna otra
 * pantalla donde se listen.
 */
export const MyQuizzes: React.FC<Props> = ({ onHost, onEdit, onCreate, onCatalogChange }) => {
  const [quizzes, setQuizzes] = useState<QuizSummary[]>([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');
  // Id del quiz cuyo borrado está pendiente de confirmar. Un `confirm()` del
  // navegador rompe el tema y se puede desactivar; esto pregunta en su sitio.
  const [confirmando, setConfirmando] = useState<string | null>(null);
  const [borrando, setBorrando] = useState<string | null>(null);
  // Carpeta elegida en las pestañas: null es «Todas» y '' es «Sin carpeta».
  // Se recuerda en este navegador, que es lo que se espera al volver al perfil.
  const [carpeta, setCarpeta] = useState<string | null>(() => {
    try {
      return localStorage.getItem('misTrivias.carpeta');
    } catch {
      return null;
    }
  });
  // Trivia cuya carpeta se está editando y el texto escrito.
  const [moviendo, setMoviendo] = useState<string | null>(null);
  const [destino, setDestino] = useState('');

  const elegirCarpeta = (c: string | null) => {
    setCarpeta(c);
    try {
      if (c === null) localStorage.removeItem('misTrivias.carpeta');
      else localStorage.setItem('misTrivias.carpeta', c);
    } catch {
      /* sin almacenamiento: la pestaña simplemente no se recuerda */
    }
  };

  // Orden natural para que «Unidad 10» no se cuele entre la 1 y la 2.
  const carpetas = useMemo(() => {
    const cuenta = new Map<string, number>();
    for (const q of quizzes) cuenta.set(q.folder ?? '', (cuenta.get(q.folder ?? '') ?? 0) + 1);
    const nombres = [...cuenta.keys()]
      .filter((c) => c !== '')
      .sort((a, b) => a.localeCompare(b, 'es', { numeric: true, sensitivity: 'base' }));
    return { nombres, cuenta, sinCarpeta: cuenta.get('') ?? 0 };
  }, [quizzes]);

  // Una carpeta recordada que ya no existe (se vació) vuelve a «Todas».
  const carpetaActiva =
    carpeta === null || carpetas.cuenta.has(carpeta) ? carpeta : null;
  const visibles =
    carpetaActiva === null ? quizzes : quizzes.filter((q) => (q.folder ?? '') === carpetaActiva);

  const mover = async (id: string) => {
    try {
      const { folder } = await api.put<{ folder: string }>(`/quizzes/${id}/folder`, { folder: destino });
      setQuizzes((prev) => prev.map((q) => (q.id === id ? { ...q, folder } : q)));
      setMoviendo(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo mover la trivia.');
    }
  };

  useEffect(() => {
    void (async () => {
      try {
        const data = await api.get<{ quizzes: QuizSummary[] }>('/quizzes/mine');
        setQuizzes(data.quizzes);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'No se pudieron cargar tus trivias.');
      } finally {
        setCargando(false);
      }
    })();
  }, []);

  const borrar = async (id: string) => {
    setBorrando(id);
    try {
      await api.delete(`/quizzes/${id}`);
      setQuizzes((prev) => prev.filter((q) => q.id !== id));
      onCatalogChange();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo borrar.');
    } finally {
      setBorrando(null);
      setConfirmando(null);
    }
  };

  return (
    <section className="space-y-3">
      <div className="flex items-center gap-3">
        <h2 className="text-lg font-display font-bold">Mis trivias</h2>
        {!cargando && quizzes.length > 0 && (
          <span className="text-sm text-ink-500">{quizzes.length}</span>
        )}
        <button
          onClick={onCreate}
          className="ml-auto flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium
                     bg-ink-850 hover:bg-ink-800 border border-ink-700 text-ink-200 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Crear
        </button>
      </div>

      {error && <p className="text-danger-400 text-sm">{error}</p>}

      {!cargando && carpetas.nombres.length > 0 && (
        <div className="flex gap-2 flex-wrap" role="tablist" aria-label="Carpetas">
          {[
            { clave: null as string | null, nombre: 'Todas', n: quizzes.length },
            ...carpetas.nombres.map((c) => ({ clave: c as string | null, nombre: c, n: carpetas.cuenta.get(c) ?? 0 })),
            ...(carpetas.sinCarpeta > 0 ? [{ clave: '' as string | null, nombre: 'Sin carpeta', n: carpetas.sinCarpeta }] : []),
          ].map(({ clave, nombre, n }) => {
            const activa = carpetaActiva === clave;
            return (
              <button
                key={clave ?? '__todas'}
                role="tab"
                aria-selected={activa}
                onClick={() => elegirCarpeta(clave)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                  activa
                    ? 'bg-ember-500 border-ember-500 text-on-accent'
                    : 'bg-ink-850 border-ink-700 text-ink-300 hover:bg-ink-800'
                }`}
              >
                {clave !== null && <Folder className="w-3.5 h-3.5" />}
                {nombre}
                <span className={activa ? 'opacity-80' : 'text-ink-500'}>{n}</span>
              </button>
            );
          })}
        </div>
      )}

      <datalist id="mis-carpetas">
        {carpetas.nombres.map((c) => (
          <option key={c} value={c} />
        ))}
      </datalist>

      {cargando ? (
        <div className="grid sm:grid-cols-2 gap-3">
          {[0, 1].map((i) => (
            <div key={i} className="surface h-28 shimmer" />
          ))}
        </div>
      ) : quizzes.length === 0 ? (
        <div className="surface p-8 text-center space-y-2">
          <p className="text-ink-300 text-sm">Todavía no has creado ninguna trivia.</p>
          <button onClick={onCreate} className="text-sky-400 hover:text-sky-300 font-medium text-sm">
            Crear la primera
          </button>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-3">
          {visibles.map((quiz) => (
            <article
              key={quiz.id}
              className="surface p-4 flex flex-col gap-3 hover:border-ink-600 transition-colors"
            >
              <div className="flex items-start gap-3">
                <span className="font-emoji text-3xl leading-none shrink-0" aria-hidden="true">
                  {quiz.coverEmoji}
                </span>
                <div className="min-w-0 flex-1">
                  <h3 className="font-semibold text-ink-50 leading-snug line-clamp-2">
                    {quiz.title}
                  </h3>
                  <p className="text-ink-500 text-xs mt-1 flex items-center gap-1.5 flex-wrap">
                    <span>
                      {quiz.questionCount} {quiz.questionCount === 1 ? 'pregunta' : 'preguntas'}
                    </span>
                    <span aria-hidden="true">·</span>
                    <span className="inline-flex items-center gap-1">
                      <Users className="w-3 h-3" />
                      {quiz.playsCount}
                    </span>
                    {quiz.visibility !== 'public' && (
                      <>
                        <span aria-hidden="true">·</span>
                        <span className="inline-flex items-center gap-1 text-ink-400">
                          {quiz.visibility === 'private' ? (
                            <Lock className="w-3 h-3" />
                          ) : (
                            <Link2 className="w-3 h-3" />
                          )}
                          {quiz.visibility === 'private' ? 'Privada' : 'No listada'}
                        </span>
                      </>
                    )}
                  </p>
                </div>
              </div>

              {moviendo === quiz.id ? (
                <form
                  className="flex items-center gap-2"
                  onSubmit={(e) => {
                    e.preventDefault();
                    void mover(quiz.id);
                  }}
                >
                  <Folder className="w-4 h-4 text-ink-400 shrink-0" />
                  <input
                    autoFocus
                    list="mis-carpetas"
                    value={destino}
                    onChange={(e) => setDestino(e.target.value)}
                    onKeyDown={(e) => e.key === 'Escape' && setMoviendo(null)}
                    maxLength={40}
                    placeholder="Carpeta (vacío = sin carpeta)"
                    aria-label={`Carpeta de ${quiz.title}`}
                    className="flex-1 min-w-0 px-3 py-2 rounded-lg text-sm bg-ink-900 border border-ink-700 text-ink-100"
                  />
                  <button
                    type="submit"
                    className="p-2 rounded-lg text-ink-300 hover:text-ink-100 hover:bg-ink-800 transition-colors"
                    aria-label="Guardar carpeta"
                    title="Guardar"
                  >
                    <Check className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setMoviendo(null)}
                    className="p-2 rounded-lg text-ink-500 hover:text-ink-100 hover:bg-ink-800 transition-colors"
                    aria-label="Cancelar"
                    title="Cancelar"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </form>
              ) : confirmando === quiz.id ? (
                <div className="flex items-center gap-2">
                  <p className="text-[0.8125rem] text-ink-300 flex-1">
                    ¿Borrar «{quiz.title}»? No se puede deshacer.
                  </p>
                  <button
                    onClick={() => void borrar(quiz.id)}
                    disabled={borrando === quiz.id}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold
                               bg-danger-500 hover:bg-danger-400 text-white transition-colors disabled:opacity-60"
                  >
                    {borrando === quiz.id && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                    Borrar
                  </button>
                  <button
                    onClick={() => setConfirmando(null)}
                    className="px-3 py-2 rounded-lg text-sm font-medium text-ink-300 hover:bg-ink-800 transition-colors"
                  >
                    No
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => onHost(quiz)}
                    className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg font-semibold text-sm
                               bg-ember-500 hover:bg-ember-400 text-on-accent transition-colors"
                  >
                    <Play className="w-3.5 h-3.5" fill="currentColor" />
                    Anfitrión
                  </button>
                  <ShareButton quiz={quiz} />
                  <button
                    onClick={() => {
                      setDestino(quiz.folder ?? '');
                      setMoviendo(quiz.id);
                    }}
                    className="p-2 rounded-lg text-ink-400 hover:text-ink-100 hover:bg-ink-800 transition-colors"
                    aria-label={`Mover ${quiz.title} a una carpeta`}
                    title="Carpeta"
                  >
                    <Folder className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => onEdit(quiz.id)}
                    className="p-2 rounded-lg text-ink-400 hover:text-ink-100 hover:bg-ink-800 transition-colors"
                    aria-label={`Editar ${quiz.title}`}
                    title="Editar"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setConfirmando(quiz.id)}
                    className="p-2 rounded-lg text-ink-500 hover:text-danger-400 hover:bg-danger-400/10 transition-colors"
                    aria-label={`Borrar ${quiz.title}`}
                    title="Borrar"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              )}
            </article>
          ))}
        </div>
      )}
    </section>
  );
};
